import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { getCashDrawerMonthly, verifyBankRun, type PendingBankDepositResponse } from "../../lib/api";
import {
  ALL_PENDING,
  checkBankRun,
  daysInScope,
  daysLeftOut,
  excludeHeldDays,
  monthLabel,
  pendingMonths,
  scopeDates,
  scopeExpectedCents,
  type LeftOutDay,
} from "../../lib/bankRun";
import { parseDollarsToCents } from "../../lib/depositVerify";
import { anyCounted, denomSummary, denomTotalCents, type DenomCounts } from "../../lib/denominations";
import DenominationCounter from "./DenominationCounter";

interface Props {
  pending: PendingBankDepositResponse;
  onRecorded: () => Promise<void> | void;
  notify: (msg: string) => void;
}

const fmt = (cents: number | null | undefined) =>
  "$" + ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSigned = (cents: number) => (cents > 0 ? "+" : cents < 0 ? "−" : "") + fmt(Math.abs(cents));
const fmtDay = (d: string) =>
  new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

/**
 * Safe-deposit banner and the bank-run modal (manager/owner only; the page
 * renders this only for them and verify_bank_run enforces it). A run is scoped
 * to one month so each month's deposit is counted and recorded on its own.
 */
export default function BankRunPanel({ pending, onRecorded, notify }: Props) {
  const months = useMemo(() => pendingMonths(pending.days), [pending.days]);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<string>(ALL_PENDING);
  const [counted, setCounted] = useState("");
  // Set while counting by denomination; the counter's total is then the count.
  const [denoms, setDenoms] = useState<DenomCounts | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [countedMyself, setCountedMyself] = useState(false);
  // null while loading; "failed" if the period could not be checked.
  const [leftOut, setLeftOut] = useState<LeftOutDay[] | "failed" | null>(null);

  const leftOutList = Array.isArray(leftOut) ? leftOut : [];
  const days = excludeHeldDays(daysInScope(pending.days, scope), leftOutList);
  const expected = scopeExpectedCents(days);
  const countedText = denoms ? (denomTotalCents(denoms) / 100).toFixed(2) : counted;
  const countedCents = parseDollarsToCents(countedText);
  // While counting by denomination, no variance until something is counted.
  const variance = countedCents == null || (denoms && !anyCounted(denoms)) ? null : countedCents - expected;

  // Days in the period the run will NOT include: a drawer still open in Square
  // (held back, its total would be partial) or bags not verified yet.
  useEffect(() => {
    if (!open) return;
    let live = true;
    const monthsToCheck = scope === ALL_PENDING ? months : [scope];
    setLeftOut(null);
    Promise.all(
      monthsToCheck.map((key) => {
        const [y, m] = key.split("-").map(Number);
        return getCashDrawerMonthly(y, m);
      })
    )
      .then((results) => live && setLeftOut(results.flatMap((r) => daysLeftOut(r.days, pending.days))))
      .catch(() => live && setLeftOut("failed"));
    return () => {
      live = false;
    };
  }, [open, scope, months, pending.days]);

  if (pending.days.length === 0 && pending.stale_runs.length === 0) return null;

  const openModal = () => {
    setScope(months[0] ?? ALL_PENDING);
    setCounted("");
    setDenoms(null);
    setNote("");
    setCountedMyself(false);
    setError(null);
    setOpen(true);
  };

  const handleConfirm = async () => {
    const check = checkBankRun({ expectedCents: expected, countedInput: countedText, note, dayCount: days.length });
    if (!check.ok) {
      setError(check.error ?? "Check the entry.");
      return;
    }
    if (!Array.isArray(leftOut)) {
      setError("The period has not been checked for open drawers yet. Wait, or close and reopen this window.");
      return;
    }
    if (!countedMyself) {
      setError("Confirm you counted this cash yourself.");
      return;
    }
    setSaving(true);
    try {
      const res = await verifyBankRun({
        counted_cents: check.countedCents!,
        // verify_bank_run stores no breakdown, so a denomination count goes in the note.
        notes: [note.trim(), denoms && denomSummary(denoms) ? `Count: ${denomSummary(denoms)}` : ""]
          .filter(Boolean)
          .join(" · ") || undefined,
        dates: scopeDates(days),
      });
      setOpen(false);
      const v = res.run.variance_cents;
      notify(
        `✓ Bank run recorded for ${monthLabel(scope)}: ${fmt(res.run.counted_cents)} counted` +
          (v === 0 ? ", no variance." : `, variance ${fmtSigned(v)}.`)
      );
      await onRecorded();
    } catch (err: any) {
      setError(err?.message || "Recording the bank run failed.");
    } finally {
      setSaving(false);
    }
  };

  const confirmLabel = saving
    ? "Recording…"
    : variance == null || variance === 0
    ? "Confirm bank run"
    : `Confirm with ${fmtSigned(variance)} variance`;

  return (
    <>
      {pending.stale_runs.length > 0 && (
        <div style={s.staleCard} role="alert">
          ⚠ {pending.stale_runs.length} bank run(s) need re-verification: a bag on a covered day was re-opened.
          {pending.stale_runs.map((r) => (
            <div key={r.id} style={s.staleLine}>
              {new Date(r.verified_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} run, {fmt(r.counted_cents)}:{" "}
              {r.stale_reason || "re-opened"}
            </div>
          ))}
        </div>
      )}
      {pending.days.length > 0 && (
        <div style={s.banner}>
          <div>
            <div style={s.bannerTitle}>🏦 Verified bags in the safe</div>
            <div style={s.bannerMeta}>
              {pending.days.length} day(s) · <strong>{fmt(pending.total_cents)}</strong> ·{" "}
              {months.map(monthLabel).join(", ")}
            </div>
          </div>
          <button type="button" style={s.goldBtn} onClick={openModal}>
            Record Bank Run
          </button>
        </div>
      )}

      {open && (
        <div className="mobile-sheet-backdrop" style={s.overlay} onClick={() => !saving && setOpen(false)}>
          <div
            className="mobile-sheet-content"
            style={s.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="bank-run-title"
          >
            <div className="mobile-sheet-handle" />
            <h2 id="bank-run-title" style={s.title}>Record Bank Run</h2>
            <p style={s.meta}>Count the bags for the period once, at the bank or before you go, and confirm the total.</p>

            <label style={s.label} htmlFor="bank-run-scope">Period</label>
            <select
              id="bank-run-scope"
              style={s.input}
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                setError(null);
              }}
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
              {months.length > 1 && <option value={ALL_PENDING}>{monthLabel(ALL_PENDING)}</option>}
            </select>

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Day</th>
                    <th style={s.th}>Register</th>
                    <th style={s.th}>Bag #</th>
                    <th style={{ ...s.th, textAlign: "right" }}>Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {days.flatMap((d) =>
                    (d.bags.length ? d.bags : [null]).map((b, i) => (
                      <tr key={d.business_date + ":" + i}>
                        <td style={s.td}>{i === 0 ? fmtDay(d.business_date) : ""}</td>
                        <td style={s.td}>{b?.device_name || "—"}</td>
                        <td style={s.td}>{b?.bag_number || "—"}</td>
                        <td style={{ ...s.td, textAlign: "right" }}>{fmt(b ? b.staff_counted_cents : d.actual_deposit_cents)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={s.tdTotal} colSpan={3}>
                      Expected ({days.length} day{days.length === 1 ? "" : "s"})
                    </td>
                    <td style={{ ...s.tdTotal, textAlign: "right" }}>{fmt(expected)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {leftOut === null && <div style={s.muted}>Checking the period for open drawers and unverified bags…</div>}
            {leftOut === "failed" && (
              <div style={s.bad}>Could not check the period for open drawers. Close this window and try again.</div>
            )}
            {leftOutList.some((d) => d.reason === "drawer-open") && (
              <div style={s.leftOut}>
                Held back, drawer still open in Square:{" "}
                {leftOutList.filter((d) => d.reason === "drawer-open").map((d) => fmtDay(d.business_date)).join(", ")}. Close
                it in Square, verify its bag, then include the day in a later run.
              </div>
            )}
            {leftOutList.some((d) => d.reason === "late-bag") && (
              <div style={s.leftOut}>
                Held back, a drawer closed after the day was verified:{" "}
                {leftOutList.filter((d) => d.reason === "late-bag").map((d) => fmtDay(d.business_date)).join(", ")}. Verify
                that bag on the Daily tab; the day&apos;s total then updates and it can go in a later run.
              </div>
            )}
            {leftOutList.some((d) => d.reason === "unverified") && (
              <div style={s.leftOut}>
                Not in this run, bags not verified yet:{" "}
                {leftOutList.filter((d) => d.reason === "unverified").map((d) => fmtDay(d.business_date)).join(", ")}. Verify
                them on the Daily tab first, or they go into a later run.
              </div>
            )}

            <div style={s.labelRow}>
              <label style={s.label} htmlFor={denoms ? undefined : "bank-run-counted"}>Total counted ($)</label>
              <button
                type="button"
                style={s.linkBtn}
                onClick={() => {
                  // Switching back to typing keeps the counted total rather than discarding it.
                  if (denoms && anyCounted(denoms)) setCounted(countedText);
                  setDenoms(denoms ? null : {});
                  setError(null);
                }}
              >
                {denoms ? "Type a total instead" : "🧮 Count by denomination"}
              </button>
            </div>
            {denoms ? (
              <>
                <DenominationCounter
                  value={denoms}
                  onChange={(next) => {
                    setDenoms(next);
                    setError(null);
                  }}
                  compact
                />
                <div style={s.totalRow}>
                  <span>Total counted</span>
                  <strong>{fmt(countedCents ?? 0)}</strong>
                </div>
              </>
            ) : (
              <input
                id="bank-run-counted"
                style={s.input}
                inputMode="decimal"
                placeholder={fmt(expected)}
                value={counted}
                onChange={(e) => {
                  setCounted(e.target.value);
                  setError(null);
                }}
              />
            )}
            {variance != null && (
              <div style={variance === 0 ? s.ok : s.bad}>
                {variance === 0 ? "✓ Matches the verified bags" : `Variance ${fmtSigned(variance)}. A note is required.`}
              </div>
            )}

            <label style={s.label} htmlFor="bank-run-note">Note</label>
            <textarea
              id="bank-run-note"
              style={{ ...s.input, height: 60 }}
              placeholder="Bank, deposit slip number; required for any variance"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setError(null);
              }}
            />
            <label style={s.check}>
              <input type="checkbox" checked={countedMyself} onChange={(e) => setCountedMyself(e.target.checked)} />
              <span>I counted this cash myself.</span>
            </label>
            {error && <div style={s.bad}>{error}</div>}

            <div style={s.actions}>
              <button type="button" style={s.btn} disabled={saving} onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button type="button" style={s.goldBtn} disabled={saving || days.length === 0 || !Array.isArray(leftOut)} onClick={handleConfirm}>
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const s: Record<string, CSSProperties> = {
  banner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    background: "rgba(249, 175, 54, 0.08)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 16,
  },
  bannerTitle: { fontWeight: 800, color: "var(--gold, #F9AF36)", fontSize: 14 },
  bannerMeta: { fontSize: 13, color: "var(--text, #F0E6D4)", marginTop: 2 },
  staleCard: {
    border: "1px solid var(--red, #E05040)",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 12,
    fontSize: 13,
    color: "var(--red, #E05040)",
    fontWeight: 700,
  },
  staleLine: { fontWeight: 400, color: "var(--text, #F0E6D4)", marginTop: 4, overflowWrap: "anywhere" },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 12,
    padding: 20,
    width: "100%",
    maxWidth: 560,
    maxHeight: "90vh",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
  },
  title: { margin: 0, fontSize: 20, color: "var(--gold, #F9AF36)" },
  meta: { margin: "0 0 6px", fontSize: 13, color: "var(--muted, #8A7A66)" },
  label: { fontSize: 11, color: "var(--muted, #8A7A66)", textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 6 },
  input: {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    color: "var(--text, #F0E6D4)",
    padding: "10px 12px",
    fontSize: 16,
    minHeight: 46,
    width: "100%",
    boxSizing: "border-box",
  },
  tableWrap: { maxHeight: 260, overflow: "auto", border: "1px solid var(--border, #2A1F14)", borderRadius: 8, marginTop: 6 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    position: "sticky",
    top: 0,
    background: "var(--surface, #13100B)",
    textAlign: "left",
    padding: "8px 10px",
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    textTransform: "uppercase",
  },
  td: { padding: "6px 10px", borderTop: "1px solid rgba(255,255,255,0.04)" },
  tdTotal: { padding: "8px 10px", fontWeight: 800, color: "var(--gold, #F9AF36)", borderTop: "1px solid var(--border, #2A1F14)" },
  labelRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 6 },
  linkBtn: {
    background: "transparent",
    border: "none",
    color: "var(--gold, #F9AF36)",
    textDecoration: "underline",
    fontSize: 13,
    cursor: "pointer",
    padding: "6px 0",
    minHeight: 32,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid rgba(249, 175, 54, 0.4)",
    color: "var(--gold, #F9AF36)",
    fontSize: 15,
  },
  leftOut: { fontSize: 12, color: "var(--gold, #F9AF36)", marginTop: 4 },
  muted: { fontSize: 12, color: "var(--muted, #8A7A66)", marginTop: 4 },
  check: { display: "flex", gap: 8, alignItems: "center", fontSize: 14, marginTop: 8, cursor: "pointer", minHeight: 32 },
  ok: { color: "var(--green, #27ae60)", fontSize: 12, fontWeight: 700 },
  bad: { color: "var(--red, #E05040)", fontSize: 12, fontWeight: 700 },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12, flexWrap: "wrap" },
  btn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 46,
  },
  goldBtn: {
    background: "var(--gold, #F9AF36)",
    border: "none",
    color: "#111",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 46,
  },
};
